// Helper to standardize Medical Billing Summary analytics tracking.
// Hides boilerplate such as suppressing property inheritance.

import { AnalyticsEvent, AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'

/** The Medical Billing Summary living-asset click events, narrowed from the full AnalyticsEvent union. */
type MedSummaryAssetEvent =
  | AnalyticsEvent.MedSummaryAssetGenerateClicked
  | AnalyticsEvent.MedSummaryAssetUpdateClicked
  | AnalyticsEvent.MedSummaryAssetRetryFailedClicked
  | AnalyticsEvent.MedSummaryAssetCompleteExcludingFailedClicked
  | AnalyticsEvent.MedSummaryAssetFileRemoved
  | AnalyticsEvent.MedSummaryAssetDownloadClicked
  | AnalyticsEvent.MedSummaryAssetChatClicked

/**
 * Track a Medical Billing Summary living-asset analytics event. Always suppresses route-derived
 * feature inheritance so no residual properties from previous events leak into this payload.
 *
 * `track` has no default fallback to `getTrackEventGlobal()`: every call site lives inside a React
 * component, so callers must always pass the `trackEvent` returned by `useAnalytics()` explicitly
 * (mirrors `trackMatterDriveSyncEvent` — see analytics/README.md, "trackEvent vs trackEventGlobal").
 */
export function trackMedSummaryEvent<T extends MedSummaryAssetEvent>(event: T, props: AnalyticsEventProperties[T], track: TrackEventFn): void {
  track(event, props, {
    suppressFeatureInheritance: true,
  })
}
